// Export Button Component
import { useState } from 'react';
import { Download, FileSpreadsheet, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { ExportFormat } from '@/types/report';

interface ExportButtonProps {
  onExport: (format: ExportFormat) => Promise<void>;
  disabled?: boolean;
  formats?: ExportFormat[];
}

export function ExportButton({ 
  onExport, 
  disabled = false,
  formats = ['excel', 'pdf', 'csv']
}: ExportButtonProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [exportingFormat, setExportingFormat] = useState<ExportFormat | null>(null);

  const handleExport = async (format: ExportFormat) => {
    setIsExporting(true);
    setExportingFormat(format);
    
    try {
      await onExport(format);
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setIsExporting(false);
      setExportingFormat(null);
    }
  };

  const formatLabels: Record<ExportFormat, { ar: string; fr: string; icon: typeof FileSpreadsheet }> = {
    excel: {
      ar: 'إكسل',
      fr: 'Excel',
      icon: FileSpreadsheet,
    },
    pdf: {
      ar: 'PDF',
      fr: 'PDF',
      icon: FileText,
    },
    csv: {
      ar: 'CSV',
      fr: 'CSV',
      icon: FileText,
    },
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled || isExporting}
          className="gap-2"
        >
          <Download className="w-4 h-4" />
          <span className="font-arabic">تصدير</span>
          <span className="text-muted-foreground">/</span>
          <span>Exporter</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {formats.map((format) => {
          const FormatIcon = formatLabels[format].icon;
          const isCurrentlyExporting = isExporting && exportingFormat === format;
          
          return (
            <DropdownMenuItem
              key={format}
              onClick={() => handleExport(format)}
              disabled={isExporting}
            >
              <FormatIcon className="w-4 h-4 mr-2" />
              <span className="flex items-center gap-2">
                <span className="font-arabic">{formatLabels[format].ar}</span>
                <span className="text-muted-foreground">/</span>
                <span>{formatLabels[format].fr}</span>
              </span>
              {isCurrentlyExporting && (
                <span className="ml-auto text-xs text-muted-foreground">
                  Export...
                </span>
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
